import type { AnalyticsData } from '../hooks/useAnalyticsData';

interface Props {
  data: AnalyticsData | null;
  loading: boolean;
}

function formatAmount(v: number): string {
  if (v >= 10000) return `¥${(v / 10000).toFixed(1)}万`;
  return `¥${v.toLocaleString('zh-CN')}`;
}

function ChangeTag({ current, last, reverse }: { current: number; last: number; reverse?: boolean }) {
  if (last === 0 && current === 0) return <span className="text-xs text-gray-400">暂无数据</span>;
  if (last === 0) return <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md">新增</span>;
  const pct = Math.round(((current - last) / last) * 100);
  const isGood = reverse ? pct <= 0 : pct >= 0;
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded-md font-medium ${isGood ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-500'}`}>
      <i className={`${pct >= 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}`} />
      {Math.abs(pct)}%
    </span>
  );
}

export default function AnalyticsOverview({ data, loading }: Props) {
  const cards = [
    {
      label: '累计总收入',
      value: loading ? '-' : formatAmount(data?.totalIncome ?? 0),
      sub: loading ? '加载中...' : `本月 ${formatAmount(data?.thisMonthIncome ?? 0)}`,
      icon: 'ri-money-dollar-circle-line',
      bg: 'bg-emerald-50',
      color: 'text-emerald-600',
      valueColor: 'text-gray-900',
      change: data ? <ChangeTag current={data.thisMonthIncome} last={data.lastMonthIncome} /> : null,
    },
    {
      label: '累计总支出',
      value: loading ? '-' : formatAmount(data?.totalExpense ?? 0),
      sub: loading ? '加载中...' : '所有支出类订单总额',
      icon: 'ri-arrow-up-circle-line',
      bg: 'bg-rose-50',
      color: 'text-rose-500',
      valueColor: 'text-rose-600',
      change: null,
    },
    {
      label: '净利润',
      value: loading ? '-' : formatAmount(data?.netProfit ?? 0),
      sub: loading ? '加载中...' : '总收入 - 总支出',
      icon: 'ri-funds-line',
      bg: 'bg-teal-50',
      color: 'text-teal-600',
      valueColor: data && data.netProfit >= 0 ? 'text-teal-600' : 'text-rose-500',
      change: null,
    },
    {
      label: '总订单数',
      value: loading ? '-' : String(data?.totalOrders ?? 0),
      sub: loading ? '加载中...' : `本月 ${data?.thisMonthOrders ?? 0} 单`,
      icon: 'ri-file-list-3-line',
      bg: 'bg-cyan-50',
      color: 'text-cyan-600',
      valueColor: 'text-gray-900',
      change: data ? <ChangeTag current={data.thisMonthOrders} last={data.lastMonthOrders} /> : null,
    },
    {
      label: '完成率',
      value: loading ? '-' : `${data && data.totalOrders > 0 ? Math.round((data.completedOrders / data.totalOrders) * 100) : 0}%`,
      sub: loading ? '加载中...' : `已完成 ${data?.completedOrders ?? 0} 单`,
      icon: 'ri-checkbox-circle-line',
      bg: 'bg-violet-50',
      color: 'text-violet-600',
      valueColor: 'text-gray-900',
      change: null,
    },
    {
      label: '人均客单价',
      value: loading ? '-' : formatAmount(data?.avgOrderValue ?? 0),
      sub: loading ? '加载中...' : '收入订单均价',
      icon: 'ri-user-star-line',
      bg: 'bg-amber-50',
      color: 'text-amber-600',
      valueColor: 'text-gray-900',
      change: null,
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {cards.map(card => (
        <div key={card.label} className="bg-white border border-gray-100 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className={`w-9 h-9 ${card.bg} rounded-xl flex items-center justify-center`}>
              <i className={`${card.icon} text-base ${card.color}`} />
            </div>
            {card.change}
          </div>
          <p className={`text-xl font-bold leading-none mb-1.5 truncate ${loading ? 'animate-pulse text-gray-200' : card.valueColor}`}>
            {card.value}
          </p>
          <p className="text-xs text-gray-400 truncate">{card.label}</p>
          <p className="text-xs text-gray-300 truncate mt-0.5">{card.sub}</p>
        </div>
      ))}
    </div>
  );
}
